<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex shrink-0 items-center justify-between space-x-2">
            <div class="flex items-center space-x-3">
                <UButton
                    icon="i-ph-arrow-left-bold"
                    color="gray"
                    variant="ghost"
                    @click="navigateTo('/credentials')"
                />
                <h1 class="text-2xl font-semibold text-gray-700">Bulk Activation History</h1>
            </div>
            <div class="flex items-center space-x-2">
                <UInputMenu
                    v-model="filters.partner"
                    :search="searchPartner"
                    class="w-52"
                    placeholder="Partner"
                    option-attribute="name"
                    leading-icon="i-ph-handshake-duotone"
                    trailing
                    nullable
                    by="id"
                />
                <UButton
                    label="Clear"
                    color="gray"
                    @click="clearFilters()"
                />
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="batches"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-clock-counter-clockwise-duotone', label: 'No bulk activation history.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #id-data="{ row }">
                    <p class="font-mono">{{ row.number.toString().padStart(8, '0') }}</p>
                </template>
                <template #name-data="{ row }">
                    <span v-if="row.name" class="text-gray-700">{{ row.name }}</span>
                    <span v-else class="text-gray-300">&mdash;</span>
                </template>
                <template #partner-data="{ row }">
                    <div class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="partners" size="sm" :placeholder="row.partner.name" :path="row.partner.avatar" />
                        <span>{{ row.partner.name }}</span>
                    </div>
                </template>
                <template #date-data="{ row }">
                    <TableDateCell :date="row.created_at" />
                </template>
                <template #total-data="{ row }">
                    <p class="font-mono">{{ row.total_records }}</p>
                </template>
                <template #success-data="{ row }">
                    <UBadge color="green" variant="subtle" :label="String(row.successful_count)" />
                </template>
                <template #duplicate-data="{ row }">
                    <UBadge color="amber" variant="subtle" :label="String(row.duplicate_count)" />
                </template>
                <template #errors-data="{ row }">
                    <UBadge color="red" variant="subtle" :label="String(row.invalid_count + row.error_count)" />
                </template>
                <template #actions-data="{ row }">
                    <UButton
                        label="View Details"
                        size="xs"
                        color="gray"
                        @click="viewBatchDetails(row)"
                    />
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <OverlayBulkActivationBatchDetails
            v-model:open="overlays.details"
            :batch="selectedBatch"
            @reload="fetchData"
        />
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import type { BulkActivationBatch } from '~/types/bulkActivation'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Bulk Activation History' })

const client = useSupabaseClient()
const pagination = usePagination()

const loading = ref(false)
const filters = ref<{ [key: string]: any }>({})
const batches = ref<BulkActivationBatch[]>([])
const selectedBatch = ref<BulkActivationBatch>()

const overlays = reactive({
    details: false,
})

const columns = [
    { key: 'id', label: 'Batch ID' },
    { key: 'name', label: 'Name' },
    { key: 'partner', label: 'Partner' },
    { key: 'date', label: 'Date' },
    { key: 'total', label: 'Total' },
    { key: 'success', label: 'Success' },
    { key: 'duplicate', label: 'Duplicate' },
    { key: 'errors', label: 'Errors' },
    { key: 'actions', class: 'w-px' },
]

onMounted(() => fetchData())
watch(filters, () => fetchData(), { deep: true })
watch(() => pagination.from, () => fetchData())

async function fetchData() {
    loading.value = true
    batches.value = []
    pagination.setTotal(undefined)

    let query = client
        .from('bulk_activation_batches')
        .select(`
            id, number, name, partner_id, user_id, status,
            total_records, successful_count, duplicate_count,
            invalid_count, error_count,
            whatsapp_sent_count, whatsapp_failed_count,
            created_at, completed_at,
            partner:partners (id, name, avatar)
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(pagination.from, pagination.to)

    if (filters.value.partner) {
        query = query.eq('partner_id', filters.value.partner.id)
    }

    const { count, data } = await query

    if (count && data) {
        batches.value = data as unknown as BulkActivationBatch[]
        pagination.setTotal(count)
    }

    loading.value = false
}

async function searchPartner(q: string) {
    const { data, error } = await client
        .from('partners')
        .select('id, name')
        .is('deleted_at', null)
        .ilike('name', `%${q}%`)

    if (error) return []
    return data
}

function viewBatchDetails(batch: BulkActivationBatch) {
    selectedBatch.value = batch
    overlays.details = true
}

function clearFilters() {
    filters.value = {}
}
</script>
